import { Table, Tbody, Td, Tr } from '~/common/components/Tables';
import Text from '~/common/components/Texts/Text';
import { formatCurrency, formatDate } from '~/common/util/format';
import type { WvoeCalculationResult } from '~/pages/IncomeCalculator/types';

import DataDisplayBlock from './DataDisplayBlock';

type WvoeTrendingSectionProps = Pick<
  WvoeCalculationResult['annualDetails'][number]['calculationResults'][number],
  | 'calculationEquation'
  | 'incomeDetails'
  | 'consideredFormulas'
  | 'monthsUsedForPriorYearCalculation'
  | 'calculatedMonthlyIncome'
> & {
  throughDate: string;
  monthsWorkedThisYear: number;
  monthsUsedForPriorYearCalculation?: string;
};

const WvoeTrendingSection = ({
  calculationEquation,
  incomeDetails,
  consideredFormulas,
  throughDate,
  monthsWorkedThisYear,
  monthsUsedForPriorYearCalculation,
  calculatedMonthlyIncome,
}: Readonly<WvoeTrendingSectionProps>) => {
  const columnHeaders = ['{fiscalYear} (YTD)', '{fiscalYear}', '{fiscalYear}'];

  const DisplayBlocksEquation = {
    containerProps: { mb: '10px', mt: '10px' },
    dlProps: { fontSize: 'sm' },
    data: [{ label: 'Calculation Equation', value: calculationEquation }],
  };

  const DisplayBlocks = [
    {
      containerProps: { mb: '20px', mt: '20px' },
      dlProps: { fontSize: 'sm' },
      data: [
        {
          label: 'YTD Months Through ' + formatDate(throughDate),
          value: monthsWorkedThisYear.toString() + ' Months',
        },
        {
          label: 'Months Used From Prior Year',
          value: monthsUsedForPriorYearCalculation || 'N/A',
        },
      ],
    },
  ];

  const threeYearFormula =
    consideredFormulas?.find(
      (item) => item.formulaType === 'ThreeYearAverageMonthlyIncome',
    )?.formula || '';

  const formulaBlocks = [
    {
      containerProps: { mb: '20px', mt: '20px' },
      dlProps: { fontSize: 'sm' },
      data: [
        { label: 'Calculation ', value: threeYearFormula },
        {
          label: 'Variable Base Income ',
          value: formatCurrency(calculatedMonthlyIncome?.toString() || ''),
        },
      ],
    },
  ];

  return (
    <>
      <DataDisplayBlock {...DisplayBlocksEquation} />

      <Table size="small" width="auto">
        <Tbody>
          {columnHeaders.map((header, idx) => {
            const h = header.replace(
              '{fiscalYear}',
              idx in incomeDetails ? String(incomeDetails[idx].fiscalYear) : '',
            );

            const fiscalYearKey =
              incomeDetails[idx]?.fiscalYear ?? `row-${idx}`;
            return (
              <Tr key={fiscalYearKey}>
                <td
                  style={{
                    borderBottom: 'none',
                    textAlign: 'right',
                    fontWeight: 'bold',
                  }}
                >
                  {h}
                </td>
                <Td
                  sx={{ borderBottom: 'none', paddingRight: '24px' }}
                  key={`totalIncome-${fiscalYearKey}`}
                >
                  {formatCurrency(incomeDetails[idx]?.totalIncome)}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      {DisplayBlocks.map((block) => (
        <DataDisplayBlock key={block.data[0]?.label} {...block} />
      ))}

      {consideredFormulas
        ?.filter(
          (consideredFormula) =>
            consideredFormula.formulaType !== 'ThreeYearAverageMonthlyIncome',
        )
        ?.map((consideredFormula) => {
          const [left, right] = consideredFormula.formula
            ? consideredFormula.formula.split('=')
            : ['', ''];
          return (
            <Text
              listStyleType="none"
              lineHeight="1"
              key={consideredFormula.formulaType}
            >
              <span style={{ fontSize: '15px', fontWeight: 'bold' }}>
                {left}{' '}
              </span>
              {
                <span style={{ fontSize: '15px', fontWeight: 'normal' }}>
                  {' '}
                  = {right}
                </span>
              }
            </Text>
          );
        })}

      {formulaBlocks.map((block) => (
        <DataDisplayBlock key={block.data[0]?.label} {...block} />
      ))}
    </>
  );
};

export default WvoeTrendingSection;
